import PersonIcon from '@material-design-icons/svg/filled/person.svg?react';
import { useRef, useState, type ChangeEvent } from 'react';
import { toast } from 'sonner';

import {
  CopyableValue,
  Field,
  FieldGrid,
  Section,
} from '../../../components/common/detailParts';
import { TypedStatusBadge } from '../../../components/common/StatusBadge';
import { Input } from '../../../components/Input';
import { S3Image } from '../../../components/S3Image';
import { UrlValidationStatus } from '../../../components/UrlValidationStatus';
import { useExternalUrlValidation } from '../../../hooks/useExternalUrlValidation';
import { useTRPC } from '../../../hooks/useTRPC';
import { captureAndReportError } from '../../../utils/errorReporting';
import {
  Callout,
  Empty,
  Flag,
} from '../../../components/common/detailListParts';
import type { SuperDappTabProps } from '../types';

const MAX_AVATAR_SIZE_BYTES = 3_000_000;
const ALLOWED_AVATAR_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
]);
const FILE_EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

const AVATAR_FALLBACK = (
  <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-white/[0.04] ring-1 ring-white/10">
    <PersonIcon className="size-6 fill-current text-neutral-500" />
  </div>
);

/** Thin ghost button — the toolbar treatment used across the detail page. */
const GHOST_BUTTON_CLASS =
  'inline-flex h-8 items-center justify-center rounded-lg border border-white/5 bg-white/[0.04] px-3 text-xs font-medium text-white/90 transition-colors hover:bg-white/[0.08] disabled:cursor-not-allowed disabled:opacity-40';
const PRIMARY_BUTTON_CLASS =
  'inline-flex h-8 items-center justify-center rounded-lg border border-teal-400/30 bg-teal-500/15 px-3 text-xs font-medium text-teal-200 transition-colors hover:border-teal-400/50 hover:bg-teal-500/25 disabled:cursor-not-allowed disabled:opacity-40';

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;

      if (typeof result !== 'string') {
        reject(new Error('Failed to read the selected image'));
        return;
      }

      const [, base64 = ''] = result.split(',');
      resolve(base64);
    };

    reader.onerror = () => {
      reject(reader.error ?? new Error('Failed to read the selected image'));
    };

    reader.readAsDataURL(file);
  });
}

function getFileExtension(file: File): string | null {
  const fileNameExtension = file.name.split('.').pop()?.toLowerCase();

  if (fileNameExtension && ALLOWED_AVATAR_EXTENSIONS.has(fileNameExtension)) {
    return fileNameExtension;
  }

  return FILE_EXTENSION_BY_MIME_TYPE[file.type] ?? null;
}

function normalizeHttpsUrl(value: string): string {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return '';
  }

  if (trimmedValue.startsWith('https://')) {
    return trimmedValue;
  }

  if (trimmedValue.startsWith('http://')) {
    return `https://${trimmedValue.slice('http://'.length)}`;
  }

  return `https://${trimmedValue}`;
}

function parsePublisherProfileInput(name: string, website: string) {
  const normalizedName = name.trim();

  if (!normalizedName) {
    throw new Error('Publisher name is required.');
  }

  const normalizedWebsite = normalizeHttpsUrl(website);

  if (!normalizedWebsite) {
    throw new Error('Publisher website is required.');
  }

  let parsedWebsite: URL;

  try {
    parsedWebsite = new URL(normalizedWebsite);
  } catch {
    throw new Error('Enter a valid publisher website.');
  }

  if (parsedWebsite.protocol !== 'https:') {
    throw new Error('Publisher website must use HTTPS.');
  }

  return {
    name: normalizedName,
    website: normalizedWebsite,
  };
}

export function PublisherTab({ data, formatDate, reload }: SuperDappTabProps) {
  const trpc = useTRPC();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const { dapp, publisher } = data;
  const { organization, individual, kybSubmission, user } = publisher;
  const publisherName = organization?.name ?? individual?.name ?? '';
  const publisherWebsite = organization?.website ?? individual?.website ?? '';
  const [publisherWebsiteDraft, setPublisherWebsiteDraft] =
    useState(publisherWebsite);
  const profileNameLabel = organization
    ? 'Organization Name'
    : 'Publisher Name';
  const avatarUrl = individual?.avatarUrl;
  const avatarThumbnailUrl = individual?.avatarThumbnailUrl;
  const hasAvatar = Boolean(avatarUrl || avatarThumbnailUrl);
  const publisherNameInputRef = useRef<HTMLInputElement>(null);
  const publisherWebsiteInputRef = useRef<HTMLInputElement>(null);
  const normalizedCurrentPublisherWebsite = publisherWebsite.trim()
    ? normalizeHttpsUrl(publisherWebsite)
    : '';
  const normalizedPublisherWebsiteDraft = publisherWebsiteDraft.trim()
    ? normalizeHttpsUrl(publisherWebsiteDraft)
    : '';
  const publisherWebsiteReachability = useExternalUrlValidation(
    normalizedPublisherWebsiteDraft,
    {
      enabled:
        isEditingProfile &&
        normalizedPublisherWebsiteDraft !== normalizedCurrentPublisherWebsite,
    },
  );

  const handleAvatarSelection = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const resetInput = () => {
      event.target.value = '';
    };

    const fileExtension = getFileExtension(file);

    if (!fileExtension) {
      toast.error('Upload a JPG, PNG, GIF, or WEBP image.');
      resetInput();
      return;
    }

    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      toast.error('Avatar images must be 3 MB or smaller.');
      resetInput();
      return;
    }

    try {
      setIsUploadingAvatar(true);

      const imageBase64 = await fileToBase64(file);
      const result = await trpc.updateSuperDappPublisherAvatar.mutate({
        dappId: dapp.id,
        imageBase64,
        fileExtension,
      });

      if (result._tag === 'Left') {
        throw new Error(result.left.message);
      }

      toast.success(result.right.message);
      await reload();
    } catch (error) {
      captureAndReportError(error, {
        operation: 'superDappDetail.updatePublisherAvatar',
        component: 'PublisherTab',
        dappId: dapp.id,
        publisherUserJoinId: publisher.id,
      });
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update publisher avatar',
      );
    } finally {
      setIsUploadingAvatar(false);
      resetInput();
    }
  };

  const handleEditProfile = () => {
    setPublisherWebsiteDraft(publisherWebsite);
    setIsEditingProfile(true);
  };

  const handleCancelProfileEdit = () => {
    setPublisherWebsiteDraft(publisherWebsite);
    setIsEditingProfile(false);
  };

  const handleSaveProfile = async () => {
    let payload: ReturnType<typeof parsePublisherProfileInput>;

    try {
      payload = parsePublisherProfileInput(
        publisherNameInputRef.current?.value ?? publisherName,
        publisherWebsiteInputRef.current?.value ?? publisherWebsite,
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to validate publisher profile',
      );
      return;
    }

    try {
      setIsSavingProfile(true);

      const result = await trpc.updateSuperDappPublisherProfile.mutate({
        dappId: dapp.id,
        ...payload,
      });

      if (result._tag === 'Left') {
        throw new Error(result.left.message);
      }

      toast.success(result.right.message);
      setIsEditingProfile(false);
      await reload();
    } catch (error) {
      captureAndReportError(error, {
        operation: 'superDappDetail.updatePublisherProfile',
        component: 'PublisherTab',
        dappId: dapp.id,
        publisherUserJoinId: publisher.id,
      });
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update publisher profile',
      );
    } finally {
      setIsSavingProfile(false);
    }
  };

  return (
    <div className="space-y-4">
      <Section
        title="Publisher"
        trailing={
          <span className="text-xs text-neutral-500">{publisher.type}</span>
        }
      >
        {(organization || individual) && (
          <div className="mb-5 rounded-xl border border-white/5 bg-white/[0.02] p-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 items-center gap-3.5">
                {individual ? (
                  <S3Image
                    src={avatarUrl}
                    thumbnailSrc={avatarThumbnailUrl}
                    alt={publisherName || 'Publisher'}
                    className="size-12 shrink-0 rounded-xl object-cover ring-1 ring-white/10"
                    fallback={AVATAR_FALLBACK}
                  />
                ) : (
                  AVATAR_FALLBACK
                )}
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-white">
                    {publisherName}
                  </div>
                  {!isEditingProfile && publisherWebsite && (
                    <a
                      href={publisherWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-0.5 block truncate text-xs text-teal-400 transition-colors hover:text-teal-300"
                    >
                      {publisherWebsite}
                    </a>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-start gap-1.5 sm:items-end">
                <div className="flex flex-wrap gap-2 sm:justify-end">
                  {individual && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        aria-label="Avatar image"
                        className="hidden"
                        onChange={handleAvatarSelection}
                      />
                      <button
                        type="button"
                        className={GHOST_BUTTON_CLASS}
                        disabled={isUploadingAvatar || isSavingProfile}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {isUploadingAvatar
                          ? 'Uploading...'
                          : hasAvatar
                            ? 'Change Avatar'
                            : 'Upload Avatar'}
                      </button>
                    </>
                  )}
                  {isEditingProfile ? (
                    <>
                      <button
                        type="button"
                        className={GHOST_BUTTON_CLASS}
                        disabled={isSavingProfile}
                        onClick={handleCancelProfileEdit}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className={PRIMARY_BUTTON_CLASS}
                        disabled={
                          isSavingProfile ||
                          publisherWebsiteReachability.blocking
                        }
                        onClick={handleSaveProfile}
                      >
                        {isSavingProfile ? 'Saving...' : 'Save Changes'}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className={GHOST_BUTTON_CLASS}
                      disabled={isUploadingAvatar || isSavingProfile}
                      onClick={handleEditProfile}
                    >
                      Edit Profile
                    </button>
                  )}
                </div>
                {individual && (
                  <p className="text-[10.5px] text-neutral-600">
                    JPG, PNG, GIF, or WEBP up to 3 MB.
                  </p>
                )}
              </div>
            </div>

            {isEditingProfile && (
              <div className="mt-4 grid gap-4 border-t border-white/5 pt-4 sm:grid-cols-2">
                <label className="block">
                  <div className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.08em] text-neutral-500">
                    {profileNameLabel}
                  </div>
                  <Input
                    ref={publisherNameInputRef}
                    name="publisherName"
                    defaultValue={publisherName}
                    required
                  />
                </label>
                <label className="block">
                  <div className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.08em] text-neutral-500">
                    Website
                  </div>
                  <Input
                    ref={publisherWebsiteInputRef}
                    name="publisherWebsite"
                    type="url"
                    defaultValue={publisherWebsite}
                    required
                    onChange={(event) =>
                      setPublisherWebsiteDraft(event.target.value)
                    }
                  />
                  {publisherWebsiteReachability.status !== 'idle' && (
                    <UrlValidationStatus
                      className="mt-1 justify-end"
                      message={publisherWebsiteReachability.message}
                      status={publisherWebsiteReachability.status}
                    />
                  )}
                </label>
              </div>
            )}
          </div>
        )}

        <FieldGrid cols={2}>
          <Field label="Publisher ID">
            <CopyableValue value={publisher.id} />
          </Field>
          {organization?.address && (
            <Field label="Address">
              <span className="text-neutral-300">{organization.address}</span>
            </Field>
          )}
          {publisher.walletAddress && (
            <Field label="Wallet">
              <CopyableValue
                value={publisher.walletAddress}
                href={`https://explorer.solana.com/address/${publisher.walletAddress}?cluster=${publisher.nftCluster || 'mainnet'}`}
              />
            </Field>
          )}
        </FieldGrid>
      </Section>

      <Section
        title="Account"
        trailing={<TypedStatusBadge type="role" status={user.role} />}
      >
        <FieldGrid>
          <Field label="Email">
            <CopyableValue value={user.email} mono={false} />
          </Field>
          <Field label="Full name">
            <span className="text-neutral-300">{user.fullName || '—'}</span>
          </Field>
          <Field label="Email verified">
            <Flag on={user.emailVerified}>
              {user.emailVerified ? 'Verified' : 'Unverified'}
            </Flag>
          </Field>
          <Field label="Onboarding">
            <span className="text-neutral-300">{user.onboardingState}</span>
          </Field>
          <Field label="User ID">
            <CopyableValue value={user.id} />
          </Field>
          <Field label="Account created">
            <span className="text-neutral-300">{formatDate(user.created)}</span>
          </Field>
        </FieldGrid>
      </Section>

      {kybSubmission && (
        <Section
          title="KYB / KYC"
          trailing={
            <TypedStatusBadge type="kyb" status={kybSubmission.status} />
          }
        >
          <FieldGrid>
            <Field label="Submission ID">
              <CopyableValue value={kybSubmission.id} />
            </Field>
            <Field label="Sumsub applicant">
              {kybSubmission.sumsubApplicantId ? (
                <CopyableValue value={kybSubmission.sumsubApplicantId} />
              ) : (
                <Empty />
              )}
            </Field>
            {kybSubmission.sumsubApplicant?.companyName && (
              <Field label="Company name">
                <span className="text-neutral-300">
                  {kybSubmission.sumsubApplicant.companyName}
                </span>
              </Field>
            )}
            <Field label="Submitted">
              <span className="text-neutral-300">
                {formatDate(kybSubmission.submittedAt)}
              </span>
            </Field>
            <Field label="Review started">
              <span className="text-neutral-300">
                {formatDate(kybSubmission.reviewStartedAt)}
              </span>
            </Field>
            <Field label="Review completed">
              <span className="text-neutral-300">
                {formatDate(kybSubmission.reviewCompletedAt)}
              </span>
            </Field>
            {kybSubmission.sumsubApplicant && (
              <>
                <Field label="Sumsub review status">
                  <span className="text-neutral-300">
                    {kybSubmission.sumsubApplicant.reviewStatus ?? '—'}
                  </span>
                </Field>
                <Field label="Last synced">
                  <span className="text-neutral-300">
                    {formatDate(kybSubmission.sumsubApplicant.lastSyncedAt)}
                  </span>
                </Field>
              </>
            )}
          </FieldGrid>

          {(kybSubmission.rejectionReason ||
            kybSubmission.additionalInfoRequested) && (
            <div className="mt-5 flex flex-col gap-2">
              {kybSubmission.rejectionReason && (
                <Callout label="Rejected:">
                  {kybSubmission.rejectionReason}
                </Callout>
              )}
              {kybSubmission.additionalInfoRequested && (
                <Callout tone="amber" label="Info requested:">
                  {kybSubmission.additionalInfoRequested}
                </Callout>
              )}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
